import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { listForms, createForm, deleteForm } from '../../lib/api';
import type { FormListItem } from '../../types/form';

export default function FormList() {
  const navigate = useNavigate();
  const [forms, setForms] = useState<FormListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newFormName, setNewFormName] = useState('');
  const [newFormDescription, setNewFormDescription] = useState('');

  useEffect(() => {
    loadForms();
  }, []);

  async function loadForms() {
    try {
      setLoading(true);
      const response = await listForms();
      setForms(response.forms);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load forms');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateForm(e: React.FormEvent) {
    e.preventDefault();
    if (!newFormName.trim()) return;

    try {
      setCreating(true);
      const result = await createForm({
        name: newFormName.trim(),
        description: newFormDescription.trim() || undefined,
      });
      setShowCreateModal(false);
      setNewFormName('');
      setNewFormDescription('');
      navigate(`/admin/builder/${result.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create form');
    } finally {
      setCreating(false);
    }
  }

  async function handleDeleteForm(id: string, name: string) {
    if (!confirm(`Are you sure you want to delete "${name}"? This cannot be undone.`)) {
      return;
    }

    try {
      await deleteForm(id);
      setForms(forms.filter((f) => f.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete form');
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Forms</h1>
          <p className="text-gray-500 mt-1">Create and manage your forms</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="btn-primary">
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          New Form
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
          <button onClick={() => setError(null)} className="ml-4 text-red-500 hover:text-red-700">
            Dismiss
          </button>
        </div>
      )}

      {/* Forms Grid */}
      {forms.length === 0 ? (
        <div className="card p-12 text-center">
          <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No forms yet</h3>
          <p className="text-gray-500 mb-6">Create your first form to get started</p>
          <button onClick={() => setShowCreateModal(true)} className="btn-primary">
            Create Form
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {forms.map((form) => (
            <div key={form.id} className="card p-6 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">{form.name}</h3>
                  <p className="text-sm text-gray-500 mt-1">/{form.slug}</p>
                </div>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    form.status === 'published'
                      ? 'bg-green-100 text-green-800'
                      : form.status === 'archived'
                      ? 'bg-gray-100 text-gray-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}
                >
                  {form.status}
                </span>
              </div>

              <div className="text-sm text-gray-500 mb-4">
                <p>Version {form.version}</p>
                <p>Updated {new Date(form.updated_at).toLocaleDateString()}</p>
              </div>

              <div className="flex items-center gap-2">
                <Link to={`/admin/builder/${form.id}`} className="btn-secondary flex-1 text-center">
                  Edit
                </Link>
                <Link to={`/admin/forms/${form.id}/submissions`} className="btn-secondary flex-1 text-center">
                  Submissions
                </Link>
                <button
                  onClick={() => handleDeleteForm(form.id, form.name)}
                  className="btn-secondary text-red-600 hover:bg-red-50 hover:border-red-300"
                  title="Delete"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="card p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Create New Form</h2>
            <form onSubmit={handleCreateForm}>
              <div className="mb-4">
                <label htmlFor="formName" className="label">
                  Form Name *
                </label>
                <input
                  type="text"
                  id="formName"
                  value={newFormName}
                  onChange={(e) => setNewFormName(e.target.value)}
                  className="input"
                  placeholder="e.g., Contact Form"
                  autoFocus
                  required
                />
              </div>
              <div className="mb-6">
                <label htmlFor="formDescription" className="label">
                  Description (optional)
                </label>
                <textarea
                  id="formDescription"
                  value={newFormDescription}
                  onChange={(e) => setNewFormDescription(e.target.value)}
                  className="input"
                  rows={3}
                  placeholder="What is this form for?"
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="btn-secondary flex-1"
                  disabled={creating}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary flex-1" disabled={creating || !newFormName.trim()}>
                  {creating ? 'Creating...' : 'Create Form'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
